import Box from '@mui/material/Box';
import type { SxProps, Theme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { palette } from '@openx/components-core/lib/theme/paletteV2.ts';
import { theme } from '@openx/components-core/lib/theme/theme.ts';
import type { TroubleshootingFunnelApi, TroubleshootingFunnelKey } from '@openx/types/troubleshootingFunnel.ts';
import { abbreviateNumericValue } from '@openx/utils/lib/abbreviateNumericValue.ts';
import { useTranslation } from 'react-i18next';
import { useFunnelTranslations } from '../hooks/useFunnelTranslations.ts';
import { calculatePercentageValues } from '../utils/calculatePercentageValues.ts';
import { FunnelStepArrow } from './FunnelStepArrow.tsx';
import { FunnelTable } from './FunnelTable.tsx';
import { SingleReasonDisplay } from './SingleReasonDisplay.tsx';
import { UpshotCodesDisplay } from './UpshotCodesDisplay.tsx';

const styles: { [key: string]: SxProps<Theme> } = {
  arrowWrapper: {
    display: 'flex',
    flexShrink: 0,
    height: '100%',
    width: 'fit-content',
  },
  rowWithTooltip: {
    alignItems: 'center',
    display: 'flex',
    gap: theme => theme.spacing(1),
  },
  wrapper: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme => theme.spacing(1.5),
  },
};

type FunnelStepProps = {
  stepCount?: number;
  data: TroubleshootingFunnelApi;
  isPreviousStepForwardingValue?: boolean;
  /**
   * Flag to indicate if this is the Last 24 Hours view.
   */
  isLast24h?: boolean;
};

const funnelKeysOrder24h = [
  'targeted_bid_requests',
  'bid_responses',
  'fills',
  'impressions',
] satisfies (keyof TroubleshootingFunnelKey)[];

const funnelKeysOrderBase = [
  'targeted_bid_requests',
  'bid_responses',
  'fills',
  'impressions',
] satisfies (keyof TroubleshootingFunnelKey)[];

export const FunnelStepRecursive = ({
  stepCount = 0,
  data,
  isPreviousStepForwardingValue = true,
  isLast24h = false,
}: FunnelStepProps) => {
  const { t } = useTranslation();
  const funnelKeysOrder = isLast24h ? funnelKeysOrder24h : funnelKeysOrderBase;
  const currentKey = funnelKeysOrder[stepCount];
  const nextKey = funnelKeysOrder[stepCount + 1];

  const currentValue = isPreviousStepForwardingValue ? data[currentKey] : 0;
  const nextValue = isPreviousStepForwardingValue ? (data[nextKey] ?? 0) : 0;
  const config = useFunnelTranslations();

  if (stepCount >= funnelKeysOrder.length) return null;

  const { discardedPercentage, forwardedPercentage, discardedValueNumeric, isCurrentStepForwardingValue } =
    calculatePercentageValues(Number(currentValue), Number(nextValue));

  // For targeted_bid_requests ("Eligible Targeted Requests") if the current value is 0
  // then it means that 100% requests were filtered by the FunnelEligibleBidRequests component
  // so we must not expand the display of this component (red arrow, table with values etc.)
  if (stepCount === 0 && currentValue === 0) isPreviousStepForwardingValue = false;

  const isLastStep = stepCount === funnelKeysOrder.length - 1;
  const stepConfig = config[currentKey];
  const showSingleReason = Boolean(stepConfig.singleDiscardReason);

  return (
    <>
      <Box sx={styles.wrapper} data-test={`funnel-step-${currentKey}`}>
        <Box>
          <Typography variant="body1Bold" component="span" color={palette.text.secondary}>
            {stepConfig.title}
          </Typography>
          <Typography variant="h2" sx={{ '::before': { display: 'none' } }} data-test="funnel-step-value">
            {abbreviateNumericValue(Number(currentValue), { allowZero: true })}
          </Typography>
        </Box>
        {!isLastStep && (
          <Box sx={{ display: 'flex', flexDirection: 'row', gap: theme.spacing(3) }}>
            <Box sx={styles.arrowWrapper}>
              <FunnelStepArrow
                forwardedPercentage={forwardedPercentage}
                discardedPercentage={discardedPercentage}
                isCurrentValueAllDiscarded={!isCurrentStepForwardingValue}
                isPreviousStepForwardingValue={isPreviousStepForwardingValue}
              />
            </Box>
            {isPreviousStepForwardingValue && (
              <FunnelTable tableTitle={stepConfig.tableTitle}>
                {showSingleReason ? (
                  <SingleReasonDisplay
                    discardedPercentage={discardedPercentage}
                    reason={stepConfig.singleDiscardReason!}
                    discardedValueNumeric={discardedValueNumeric}
                    volumeColumnLabel={stepConfig.volumeColumnLabel!}
                  />
                ) : data.upshot_codes && Object.keys(data.upshot_codes).length ? (
                  <UpshotCodesDisplay data={data.upshot_codes} />
                ) : (
                  <Typography variant="body2" color="textDisabled">
                    {t('N/A')}
                  </Typography>
                )}
              </FunnelTable>
            )}
          </Box>
        )}
      </Box>
      <FunnelStepRecursive
        stepCount={stepCount + 1}
        data={data}
        isPreviousStepForwardingValue={isCurrentStepForwardingValue}
        isLast24h={isLast24h}
      />
    </>
  );
};
